import { Button } from '@progress/kendo-react-buttons';
import { useLocalStorage, useSessionStorage } from 'usehooks-ts';
import React, { useContext, useEffect, useRef, useState } from 'react';

import CameraModal from '../camera-modal';
import { getAvatars } from '../../services/players';
import { PreGameWizard } from '../../types/pre-game';
import { PlayerContext } from '../../contexts/player';
import { AvatarImage } from '../../types/tab-content';
import { OverlayContext } from '../../contexts/overlay';
import { WizardContent, AvatarPreview, AvatarLibrary } from '.';
import { GameDocumentContext } from '../../contexts/game-document';
import { UpdatePlayerStateAsync } from '../../utils/game-engine/base';
import { DisplayLanguageContext } from '../../contexts/display-languages';
import { generateTitleById } from '../../utils/game-document/display-languages';

const dataURLtoBlob = (dataURL: string): Blob => {
  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new Blob([u8arr], { type: mime });
};

const StepFour = ({ nextStepCallback, currentStep = 1 }: PreGameWizard) => {
  const [playerState, setPlayerState] = useContext(PlayerContext);
  const [state] = useContext(GameDocumentContext);
  const [displayLanguageContext] = useContext(DisplayLanguageContext);
  const [overlay, setOverlay] = useContext(OverlayContext);

  const avatarUpload = useRef<HTMLInputElement>(null);
  const [avatarList, setAvatarList] = useState<AvatarImage[]>([]);
  const [isAvatarLibraryShow, setIsAvatarLibraryShow] =
    useState<boolean>(false);
  const [selectedAvatar, setSelectedAvatar] = useSessionStorage(
    'last-avatar',
    playerState.playerState?.avatarImage ?? ''
  );
  const [cameraOpen, setCameraOpen, removeCameraOpen] = useLocalStorage(
    'camera-open',
    false
  );

  const profilePreGamePlayerJoin =
    state.gameDocument?.settings.preGame.profilePreGamePlayerJoin ?? 'avatar';

  useEffect(() => {
    if (playerState.gameCode !== undefined) {
      getDefaultAvatars(playerState.gameCode);
    }
    //eslint-disable-next-line
  }, []);

  const handleMoreButtonClicked = () => {
    setIsAvatarLibraryShow(true);
  };

  const handleChangeFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files![0];
    const url = URL.createObjectURL(file);
    setAvatar(url);
    nextStepCallback(currentStep + 1);
  };

  const handleUploadAvatar = () => {
    avatarUpload.current?.click();
  };

  const handleAvatarClick = (url: string) => {
    setAvatar(url);
    nextStepCallback(currentStep + 1);
  };

  const getDefaultAvatars = async (gameCode: string) => {
    const response = await getAvatars(gameCode);
    if (response !== undefined) {
      setAvatarList(
        response.map((item: string, index: number) => ({
          id: index,
          src: item
        }))
      );
    }
  };

  const setAvatar = (selectedAvatar: string) => {
    setSelectedAvatar(selectedAvatar);
    setPlayerState((prev) =>
      UpdatePlayerStateAsync(prev, {
        ...prev.playerState!,
        avatarImage: selectedAvatar
      })
    );
  };

  const turnOnCameraClick = async () => {
    if (!overlay.isCameraPermissionGranted) {
      setOverlay((prev) => ({ ...prev, isCameraPermissionGranted: true }));
    }

    await new Promise((r) => setTimeout(r, 500));
    setCameraOpen(true);
  };

  const handleCapture = React.useCallback(
    (screenShot: string) => {
      try {
        const imageBlob = dataURLtoBlob(screenShot);
        const extension = imageBlob.type.split('/')[1];

        const file = new File([imageBlob], `avatar.${extension}`, {
          type: imageBlob.type
        });
        const url = URL.createObjectURL(file);

        setAvatar(url);
        nextStepCallback(currentStep + 1);
      } catch (error) {
        console.error(error);
      } finally {
        removeCameraOpen();
      }
    },
    [state.gameDocument]
  );

  return (
    <>
      <WizardContent
        additionalContainerClass={'position-relative'}
        wizardTitle={
          isAvatarLibraryShow
            ? 'Choose an avatar from our library'
            : generateTitleById(
                '4e040b03-759f-49e3-b48a-7d0c0a1914e9',
                state,
                displayLanguageContext.displayLanguageSelected.resources!,
                'game'
              ) || 'Upload an image of your choice'
        }
        isShowButton={false}>
        {isAvatarLibraryShow ? (
          <div
            className={
              'd-flex flex-wrap justify-content-between align-items-center my-4 '
            }>
            <AvatarLibrary
              items={avatarList}
              handleAvatarClick={handleAvatarClick}
              isButtonMoreShow={false}
            />
          </div>
        ) : (
          <>
            <div className={'d-flex flex-column gap-2'}>
              <AvatarPreview
                avatarSource={selectedAvatar}
                handleUploadAvatar={handleUploadAvatar}>
                <input
                  type={'file'}
                  ref={avatarUpload}
                  onChange={handleChangeFile}
                  style={{ opacity: '0' }}
                  accept={'image/*'}
                />
              </AvatarPreview>
              {(profilePreGamePlayerJoin === 'avatarOrPhoto' ||
                profilePreGamePlayerJoin === 'photo') && (
                <div className={'px-4'}>
                  <Button
                    className={'w-100 upload-button p-2'}
                    onClick={turnOnCameraClick}>
                    <div
                      className={
                        'd-flex justify-content-center align-items-center gap-3'
                      }>
                      <span
                        className={
                          'material-symbols-outlined mr-2 text-success'
                        }
                        role={'button'}>
                        add_a_photo
                      </span>
                      <span>{'Take photo'}</span>
                    </div>
                  </Button>
                </div>
              )}
            </div>

            {(profilePreGamePlayerJoin === 'avatar' ||
              profilePreGamePlayerJoin === 'avatarOrPhoto') && (
              <>
                <p className={'text-center text-dark fw-bold my-4'}>
                  {generateTitleById(
                    '750604c5-d26a-4dfb-9db1-25eda7a6d95f',
                    state,
                    displayLanguageContext.displayLanguageSelected.resources!,
                    'game'
                  ) || 'Or choose an avatar from our library'}
                </p>
                <div
                  className={
                    'd-flex mb-4 justify-content-between align-items-center position-relative'
                  }>
                  <AvatarLibrary
                    limit={4}
                    items={avatarList}
                    handleAvatarClick={handleAvatarClick}
                    handleMoreButtonClicked={handleMoreButtonClicked}
                    isButtonMoreShow={true}
                  />
                </div>
              </>
            )}
          </>
        )}

        <div className={'d-flex flex-column align-items-center'}></div>
      </WizardContent>

      <CameraModal
        isOpen={cameraOpen}
        onClose={removeCameraOpen}
        onCapture={handleCapture}
      />
    </>
  );
};

export default StepFour;
